"use client";

import { useState } from "react";
import Link from "next/link";
import { Menu, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";
import { Wordmark } from "@/components/wordmark";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

type NavLink = { href: string; label: string };

const MARKETING_LINKS: NavLink[] = [
  { href: "/#how-it-works", label: "How it works" },
  { href: "/#coverage", label: "Coverage" },
  { href: "/pricing", label: "Pricing" },
];

const APP_LINKS: NavLink[] = [
  { href: "/dashboard", label: "Lookup" },
  { href: "/billing", label: "Billing" },
  { href: "/account", label: "Account" },
];

export function SiteHeader() {
  const { user, loading, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const links = user ? APP_LINKS : MARKETING_LINKS;

  return (
    // bg-paper/85 silently generated NOTHING (85 is not in Tailwind's default
    // opacity scale), so the sticky header had always been fully transparent
    // behind its blur. /80 is the nearest real step.
    <header className="sticky top-0 z-50 border-b border-border bg-paper/80 backdrop-blur-sm">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6">
        <Wordmark />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 md:flex">
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-body-sm font-medium text-muted transition-colors hover:text-ink"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <ThemeToggle />
          {loading ? null : user ? (
            <Button variant="ghost" size="sm" onClick={() => void signOut()}>
              Log out
            </Button>
          ) : (
            <>
              <Link
                href="/login"
                className="text-body-sm font-medium text-muted transition-colors hover:text-ink"
              >
                Log in
              </Link>
              <Button asChild size="sm">
                <Link href="/signup">Get started</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile: theme toggle stays outside the collapsible menu. */}
        <div className="flex items-center gap-1 md:hidden">
          <ThemeToggle />
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-[8px] text-ink"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={cn("border-t border-border bg-paper md:hidden", open ? "block" : "hidden")}>
        <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3 sm:px-6">
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="rounded-[8px] px-3 py-2.5 text-body font-medium text-text hover:bg-ink/[0.05]"
            >
              {link.label}
            </Link>
          ))}
          <div className="mt-2 flex flex-col gap-2 border-t border-border pt-3">
            {user ? (
              <Button
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  void signOut();
                }}
              >
                Log out
              </Button>
            ) : (
              <>
                <Button asChild variant="outline">
                  <Link href="/login" onClick={() => setOpen(false)}>
                    Log in
                  </Link>
                </Button>
                <Button asChild>
                  <Link href="/signup" onClick={() => setOpen(false)}>
                    Get started
                  </Link>
                </Button>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}
